/**
 * HomePage Component
 *
 * Public landing page for AttuneWell.
 * Full marketing page with hero, features, dynamic pricing, and CTAs.
 * Pricing section fetches plans from the API with static fallback.
 */

import { Navigate, Link, useNavigate } from "react-router-dom";
import {
  HeartHandshake,
  Sparkles,
  Shield,
  ArrowRight,
  Check,
  Mic,
  MessageSquare,
  Brain,
  PlayCircle,
  BarChart3,
  Lock,
  Zap,
  Calendar,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { AuthTransition } from "../components/AuthTransition";
import { useAuthStatus } from "../hooks/useAuthStatus";
import { useAuth } from "../hooks/useAuth";
import { usePublicPlans } from "../hooks/useBilling";
import { useOnboarding } from "../hooks/useOnboarding";
import type { SubscriptionPlan } from "../types/billing";

/** Static fallback plans shown when the API is unavailable */
const FALLBACK_PLANS: SubscriptionPlan[] = [
  {
    id: "fallback-free",
    name: "Free",
    description: "Perfect for trying out",
    price_cents: 0,
    price_dollars: 0,
    currency: "usd",
    trial_days: 0,
    trial_conversations: 3,
    features: {
      recordings_per_month: 3,
      ai_analysis: "basic",
      therapy_frameworks: ["general"],
      transcription: true,
      insights: true,
    },
    is_free_plan: true,
    sort_order: 0,
  },
  {
    id: "fallback-pro",
    name: "Pro",
    description: "For serious growth",
    price_cents: 2900,
    price_dollars: 29,
    currency: "usd",
    trial_days: 14,
    trial_conversations: 3,
    features: {
      recordings_per_month: "unlimited",
      ai_analysis: "advanced",
      therapy_frameworks: "all",
      transcription: true,
      insights: true,
      sentiment_analysis: true,
      ai_therapist_chat: true,
      progress_tracking: true,
      priority_support: true,
    },
    is_free_plan: false,
    sort_order: 1,
  },
];

/** Render a human-readable label for a feature key/value pair */
function featureLabel(key: string, value: unknown): string | null {
  if (value === false || value === null || value === undefined) return null;
  const labels: Record<string, string> = {
    recordings_per_month:
      value === "unlimited"
        ? "Unlimited recordings"
        : `${value} recordings per month`,
    ai_analysis:
      value === "advanced" ? "Advanced AI analysis" : "Basic AI analysis",
    therapy_frameworks:
      value === "all"
        ? "All therapy frameworks"
        : Array.isArray(value)
          ? "General therapy framework"
          : String(value),
    transcription: "Transcription & insights",
    insights: "Conversation insights",
    sentiment_analysis: "Advanced sentiment analysis",
    ai_therapist_chat: "AI therapist chat",
    progress_tracking: "Progress tracking & trends",
    priority_support: "Priority support",
    relationship_tracker: "Relationship tracker",
  };
  return labels[key] ?? null;
}

/** Get displayable feature list from a plan's features JSON */
function getPlanFeatures(plan: SubscriptionPlan): string[] {
  if (!plan.features) return [];
  const features: string[] = [];
  for (const [key, value] of Object.entries(plan.features)) {
    const label = featureLabel(key, value);
    if (label && !features.includes(label)) features.push(label);
  }
  return features;
}

export function HomePage() {
  const { isAuthenticated } = useAuthStatus();
  const { login, isAuthenticating } = useAuth();
  const { data: apiPlans, isError } = usePublicPlans();
  const { isOnboardingComplete, isLoading: onboardingLoading } = useOnboarding({
    enabled: isAuthenticated,
  });
  const navigate = useNavigate();

  const plans =
    apiPlans && apiPlans.length > 0 && !isError ? apiPlans : FALLBACK_PLANS;
  const sortedPlans = [...plans].sort((a, b) => a.sort_order - b.sort_order);

  const handleGetStarted = () => {
    navigate("/get-started");
  };

  const handleSignIn = () => {
    login();
  };

  const handleSelectPlan = (planId: string) => {
    localStorage.setItem("pendingCheckoutPlanId", planId);
    navigate("/get-started");
  };

  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  // If authenticated, redirect based on onboarding state
  if (isAuthenticated) {
    // Wait for onboarding data before deciding where to redirect
    if (onboardingLoading) {
      return <AuthTransition message="Loading..." />;
    }
    // Users who haven't completed onboarding go to the wizard
    if (!isOnboardingComplete) {
      return <Navigate to="/onboarding" replace />;
    }
    const pendingPlanId = localStorage.getItem("pendingCheckoutPlanId");
    if (pendingPlanId) {
      localStorage.removeItem("pendingCheckoutPlanId");
      return (
        <Navigate
          to={`/account?tab=billing&auto_checkout=${encodeURIComponent(pendingPlanId)}`}
          replace
        />
      );
    }
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="min-h-screen bg-background">
      {isAuthenticating && <AuthTransition message="Signing you in..." />}
      {/* Navigation */}
      <nav className="border-b border-border bg-background/80 backdrop-blur sticky top-0 z-50">
        <div className="container mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <HeartHandshake className="w-6 h-6 text-primary" />
            <span className="text-xl font-bold text-foreground">
              AttuneWell
            </span>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              onClick={() => scrollTo("pricing")}
              className="hidden sm:inline-flex"
            >
              Pricing
            </Button>
            <Button variant="ghost" onClick={handleSignIn}>
              Sign In
            </Button>
            <Button onClick={handleGetStarted}>Get Started</Button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="container mx-auto px-4 py-20 md:py-32">
        <div className="max-w-4xl mx-auto text-center space-y-8">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium">
            <Sparkles className="w-4 h-4" />A Love Letter to Better
            Communication
          </div>
          <h1 className="text-5xl md:text-6xl font-bold tracking-tight text-foreground">
            Understand Your Relationship
            <span className="text-primary"> Through AI-Powered Insights</span>
          </h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto leading-relaxed">
            For anyone who's ever struggled with attunement and communication.
            Upload your conversations, receive science-backed therapeutic
            analysis, and discover patterns that strengthen your bond.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
            <Button size="lg" onClick={handleGetStarted} className="gap-2">
              Start Free Trial
              <ArrowRight className="w-4 h-4" />
            </Button>
            <Button
              variant="secondary"
              size="lg"
              onClick={() => scrollTo("features")}
            >
              Learn More
            </Button>
          </div>
          <div className="flex items-center justify-center gap-6 pt-4 text-sm text-muted-foreground">
            <div className="flex items-center gap-2">
              <Shield className="w-4 h-4 text-primary" />
              <span>Secure & Private</span>
            </div>
            <div className="flex items-center gap-2">
              <Brain className="w-4 h-4 text-primary" />
              <span>Science-Backed</span>
            </div>
            <div className="flex items-center gap-2">
              <Lock className="w-4 h-4 text-primary" />
              <span>Encrypted</span>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="container mx-auto px-4 py-20 border-t border-border bg-muted/30">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              How AttuneWell Works
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Three simple steps to deeper understanding
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="relative">
              <div className="text-center space-y-4">
                <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto border-2 border-primary/20">
                  <Mic className="w-8 h-8 text-primary" />
                </div>
                <div className="space-y-2">
                  <div className="text-sm font-semibold text-primary">
                    Step 1
                  </div>
                  <h3 className="text-xl font-semibold text-foreground">
                    Upload Your Conversation
                  </h3>
                  <p className="text-muted-foreground text-sm">
                    Record a conversation with your partner and upload it
                    securely. We support M4A audio files with automatic speaker
                    identification.
                  </p>
                </div>
              </div>
              <div className="hidden md:block absolute top-8 -right-4 w-8 h-0.5 bg-gradient-to-r from-primary/50 to-transparent" />
            </div>

            <div className="relative">
              <div className="text-center space-y-4">
                <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto border-2 border-primary/20">
                  <Brain className="w-8 h-8 text-primary" />
                </div>
                <div className="space-y-2">
                  <div className="text-sm font-semibold text-primary">
                    Step 2
                  </div>
                  <h3 className="text-xl font-semibold text-foreground">
                    AI Analysis
                  </h3>
                  <p className="text-muted-foreground text-sm">
                    Our AI therapist analyzes your conversation using your
                    chosen framework—EFT, CBT, Gottman Method, Attachment
                    Theory, or General approach.
                  </p>
                </div>
              </div>
              <div className="hidden md:block absolute top-8 -right-4 w-8 h-0.5 bg-gradient-to-r from-primary/50 to-transparent" />
            </div>

            <div className="text-center space-y-4">
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto border-2 border-primary/20">
                <Sparkles className="w-8 h-8 text-primary" />
              </div>
              <div className="space-y-2">
                <div className="text-sm font-semibold text-primary">Step 3</div>
                <h3 className="text-xl font-semibold text-foreground">
                  Get Insights
                </h3>
                <p className="text-muted-foreground text-sm">
                  Receive detailed insights, sentiment analysis, and actionable
                  feedback. Chat with our AI therapist about any part of your
                  conversation.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section
        id="features"
        className="container mx-auto px-4 py-20 border-t border-border"
      >
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              Powerful Features for Deeper Connection
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Everything you need to understand and improve your relationship
              communication
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="p-6 rounded-xl border border-border bg-card hover:shadow-lg transition-shadow">
              <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                <Mic className="w-6 h-6 text-primary" />
              </div>
              <h3 className="text-lg font-semibold text-foreground mb-2">
                Smart Transcription
              </h3>
              <p className="text-sm text-muted-foreground">
                Automatic transcription with speaker identification, timestamps,
                and karaoke-style navigation. Click any text to jump to that
                moment.
              </p>
            </div>

            <div className="p-6 rounded-xl border border-border bg-card hover:shadow-lg transition-shadow">
              <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                <Brain className="w-6 h-6 text-primary" />
              </div>
              <h3 className="text-lg font-semibold text-foreground mb-2">
                Multiple Therapy Frameworks
              </h3>
              <p className="text-sm text-muted-foreground">
                Choose from EFT, CBT, Gottman Method, Attachment Theory,
                Narrative Therapy, or Solution-Focused approaches.
              </p>
            </div>

            <div className="p-6 rounded-xl border border-border bg-card hover:shadow-lg transition-shadow">
              <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                <Sparkles className="w-6 h-6 text-primary" />
              </div>
              <h3 className="text-lg font-semibold text-foreground mb-2">
                Conversation Insights
              </h3>
              <p className="text-sm text-muted-foreground">
                Get detailed insights for each segment—identify patterns, repair
                attempts, defensiveness, and communication strengths.
              </p>
            </div>

            <div className="p-6 rounded-xl border border-border bg-card hover:shadow-lg transition-shadow">
              <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                <BarChart3 className="w-6 h-6 text-primary" />
              </div>
              <h3 className="text-lg font-semibold text-foreground mb-2">
                Sentiment Analysis
              </h3>
              <p className="text-sm text-muted-foreground">
                Real-time sentiment tracking shows emotional patterns,
                validating moments, and areas needing attention throughout your
                conversation.
              </p>
            </div>

            <div className="p-6 rounded-xl border border-border bg-card hover:shadow-lg transition-shadow">
              <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                <MessageSquare className="w-6 h-6 text-primary" />
              </div>
              <h3 className="text-lg font-semibold text-foreground mb-2">
                AI Therapist Chat
              </h3>
              <p className="text-sm text-muted-foreground">
                Ask questions about any moment in your conversation. Get
                personalized guidance and deeper understanding from our neutral
                AI therapist.
              </p>
            </div>

            <div className="p-6 rounded-xl border border-border bg-card hover:shadow-lg transition-shadow">
              <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                <PlayCircle className="w-6 h-6 text-primary" />
              </div>
              <h3 className="text-lg font-semibold text-foreground mb-2">
                Interactive Playback
              </h3>
              <p className="text-sm text-muted-foreground">
                Navigate your conversation effortlessly with synchronized audio,
                transcript, and insights. Jump to any moment instantly.
              </p>
            </div>

            <div className="p-6 rounded-xl border border-border bg-card hover:shadow-lg transition-shadow">
              <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                <Calendar className="w-6 h-6 text-primary" />
              </div>
              <h3 className="text-lg font-semibold text-foreground mb-2">
                Relationship Tracker
              </h3>
              <p className="text-sm text-muted-foreground">
                Log daily entries about your relationship with calendar-based
                tracking. Discuss patterns and trends with an AI coach to build
                healthier habits.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Social Proof / Trust Section */}
      <section className="container mx-auto px-4 py-20 border-t border-border bg-muted/30">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              Built on Science, Designed for Privacy
            </h2>
            <p className="text-lg text-muted-foreground">
              Your conversations are encrypted, secure, and never shared
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center space-y-3">
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
                <Shield className="w-8 h-8 text-primary" />
              </div>
              <h3 className="font-semibold text-foreground">
                End-to-End Encryption
              </h3>
              <p className="text-sm text-muted-foreground">
                Your recordings are encrypted and stored securely. Only you have
                access.
              </p>
            </div>

            <div className="text-center space-y-3">
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
                <Brain className="w-8 h-8 text-primary" />
              </div>
              <h3 className="font-semibold text-foreground">Evidence-Based</h3>
              <p className="text-sm text-muted-foreground">
                Analysis grounded in proven therapeutic frameworks and
                relationship science.
              </p>
            </div>

            <div className="text-center space-y-3">
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
                <Zap className="w-8 h-8 text-primary" />
              </div>
              <h3 className="font-semibold text-foreground">Fast Processing</h3>
              <p className="text-sm text-muted-foreground">
                Get your analysis in minutes, not days. Start understanding
                immediately.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section
        id="pricing"
        className="container mx-auto px-4 py-20 border-t border-border"
      >
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              Simple, Transparent Pricing
            </h2>
            <p className="text-lg text-muted-foreground">
              Start free, upgrade when you're ready
            </p>
          </div>

          <div
            className={`grid gap-8 ${sortedPlans.length === 1 ? "max-w-md mx-auto" : sortedPlans.length >= 3 ? "md:grid-cols-3" : "md:grid-cols-2"}`}
          >
            {sortedPlans.map((plan) => {
              const isFree = plan.is_free_plan;
              const features = getPlanFeatures(plan);

              return (
                <div
                  key={plan.id}
                  className={
                    isFree
                      ? "p-8 rounded-xl border border-border bg-card"
                      : "p-8 rounded-xl border-2 border-primary bg-primary/5 relative"
                  }
                >
                  {!isFree && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-primary text-primary-foreground text-xs font-medium rounded-full">
                      Popular
                    </div>
                  )}
                  <h3 className="text-2xl font-bold text-foreground mb-2">
                    {plan.name}
                  </h3>
                  {plan.description && (
                    <p className="text-muted-foreground mb-6">
                      {plan.description}
                    </p>
                  )}
                  <div className="mb-6">
                    <span className="text-4xl font-bold text-foreground">
                      ${Math.round(plan.price_dollars)}
                    </span>
                    <span className="text-muted-foreground">/month</span>
                  </div>
                  {features.length > 0 && (
                    <ul className="space-y-3 mb-8">
                      {features.map((feature) => (
                        <li
                          key={feature}
                          className="flex items-center gap-2 text-sm"
                        >
                          <Check className="w-4 h-4 text-primary flex-shrink-0" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  <Button
                    variant={isFree ? "secondary" : "default"}
                    className="w-full"
                    onClick={
                      isFree
                        ? handleGetStarted
                        : () => handleSelectPlan(plan.id)
                    }
                  >
                    {isFree ? "Get Started" : "Start Free Trial"}
                  </Button>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="container mx-auto px-4 py-20 border-t border-border">
        <div className="max-w-3xl mx-auto text-center space-y-8">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground">
            Ready to Understand Your Relationship Better?
          </h2>
          <p className="text-lg text-muted-foreground">
            Join couples who are building stronger connections through better
            communication.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" onClick={handleGetStarted} className="gap-2">
              Start Your Free Trial
              <ArrowRight className="w-4 h-4" />
            </Button>
            <Button variant="secondary" size="lg" onClick={handleSignIn}>
              Sign In
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border bg-muted/30 py-12">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-2">
              <HeartHandshake className="w-5 h-5 text-primary" />
              <span className="font-bold text-foreground">AttuneWell</span>
            </div>
            <div className="flex items-center gap-4 text-sm text-muted-foreground">
              <Link
                to="/legal"
                className="hover:text-foreground transition-colors"
              >
                Legal
              </Link>
              <span>|</span>
              <span>&copy; 2026 AttuneWell. All rights reserved.</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
